import { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { uid } from "../utils/helpers";

const AppContext = createContext(null);

// Decode the role from a JWT so login can route isolated-role users (copywriter,
// redactor, dev, sem, ppc…) to their cabinet immediately — without waiting for a
// page reload / /verify. (JWT payload is base64url; role is plain ASCII.)
function roleFromToken(tok) {
  try {
    let b = String(tok).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    b += "=".repeat((4 - (b.length % 4)) % 4);
    return JSON.parse(atob(b)).role || "user";
  } catch { return "user"; }
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};

export function AppProvider({ children }) {
  const [view, _setView] = useState(() => localStorage.getItem("metago_view") || "list");
  const viewHistory = useRef([]);
  const [infoId, setInfoId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [activeId, _setActiveId] = useState(() => localStorage.getItem("metago_activeId") || null);

  const setView = (v) => {
    const cur = view;
    if (cur && cur !== v) {
      viewHistory.current = [...viewHistory.current.slice(-20), cur];
    }
    _setView(v);
    localStorage.setItem("metago_view", v);
  };
  const goBack = () => {
    const hist = viewHistory.current;
    if (hist.length > 0) {
      const prev = hist.pop();
      viewHistory.current = hist;
      _setView(prev);
      localStorage.setItem("metago_view", prev);
    }
  };
  const canGoBack = viewHistory.current.length > 0;
  const setActiveId = (id) => { _setActiveId(id); if (id) localStorage.setItem("metago_activeId", id); else localStorage.removeItem("metago_activeId"); };
  const [toast, setToast] = useState("");
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem("metago_dark");
    return saved !== null ? saved === "true" : true;
  });
  const dataRef = useRef({});

  // ── Device ID ──
  const getDeviceId = () => {
    let id = localStorage.getItem("metago_device_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("metago_device_id", id);
    }
    return id;
  };

  // ── Auth state ──
  const [authed, setAuthed] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [currentUser, setCurrentUser] = useState("");
  const [loginPending, setLoginPending] = useState(false);
  const [loginCode, setLoginCode] = useState("");
  const [loginNeedCode, setLoginNeedCode] = useState(false);
  const [loginCodeInput, setLoginCodeInput] = useState("");
  const [userRole, setUserRole] = useState("user");

  useEffect(() => {
    const token = localStorage.getItem("metago_token");
    if (!token) { setAuthLoading(false); return; }
    fetch("/api/verify", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.valid) { setAuthed(true); setCurrentUser(d.username); setUserRole(d.role || "user"); } else { localStorage.removeItem("metago_token"); } })
      .catch(() => { localStorage.removeItem("metago_token"); })
      .finally(() => setAuthLoading(false));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const r = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: loginUser, password: loginPass, device_id: getDeviceId() })
      });
      const d = await r.json();
      if (r.ok && d.pending) {
        setLoginPending(true);
        setLoginCode(d.code);
      } else if (r.ok && d.token) {
        localStorage.setItem("metago_token", d.token);
        setUserRole(roleFromToken(d.token));
        setAuthed(true);
        setCurrentUser(d.username);
      } else if (!r.ok) {
        setLoginError(d.error || "Login failed");
      }
    } catch { setLoginError("Connection error"); }
  };

  // Poll for 2FA result
  useEffect(() => {
    if (!loginPending || !loginCode || loginNeedCode) return;
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`/api/login/check?code=${loginCode}&device_id=${getDeviceId()}`);
        const d = await r.json();
        if (d.status === "ok" && d.token) {
          clearInterval(interval);
          localStorage.setItem("metago_token", d.token);
          setUserRole(roleFromToken(d.token));
          setLoginPending(false);
          setLoginCode("");
          setAuthed(true);
          setCurrentUser(d.username || "");
        } else if (d.status === "code_sent") {
          clearInterval(interval);
          setLoginNeedCode(true);
        } else if (d.status === "rejected") {
          clearInterval(interval);
          setLoginPending(false);
          setLoginCode("");
          setLoginError("Login rejected");
        } else if (d.status === "expired") {
          clearInterval(interval);
          setLoginPending(false);
          setLoginCode("");
          setLoginError("Confirmation timed out (5 min)");
        }
      } catch {}
    }, 2000);
    return () => clearInterval(interval);
  }, [loginPending, loginCode, loginNeedCode]);

  // Submit 6-digit verify code
  const handleVerifyCode = async () => {
    setLoginError("");
    try {
      const r = await fetch("/api/login/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: loginCode, verify_code: loginCodeInput, device_id: getDeviceId() })
      });
      const d = await r.json();
      if (d.status === "approved" && d.token) {
        localStorage.setItem("metago_token", d.token);
        setUserRole(roleFromToken(d.token));
        setAuthed(true);
        setCurrentUser(d.username);
        setLoginPending(false);
        setLoginCode("");
        setLoginNeedCode(false);
        setLoginCodeInput("");
      } else if (d.status === "rejected") {
        setLoginPending(false);
        setLoginCode("");
        setLoginNeedCode(false);
        setLoginCodeInput("");
        setLoginError("Too many wrong attempts. Try again.");
      } else if (d.status === "expired") {
        setLoginPending(false);
        setLoginCode("");
        setLoginNeedCode(false);
        setLoginCodeInput("");
        setLoginError("Code expired (5 min)");
      } else if (d.status === "wrong_code") {
        setLoginError(d.error || "Wrong code");
      }
    } catch { setLoginError("Connection error"); }
  };

  const handleLogout = () => {
    localStorage.removeItem("metago_token");
    setAuthed(false);
    setCurrentUser("");
    setLoginUser("");
    setLoginPass("");
  };

  // ── API helper ──
  const apiFetch = useCallback(async (path, opts = {}) => {
    const token = localStorage.getItem("metago_token");
    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(path, { ...opts, headers });
    if (res.status === 401) {
      // Only treat as session expiry when the server reports a real auth failure.
      // Data endpoints (e.g. GA4/GSC "not connected") may also return 401 — those must NOT log the user out.
      let sessionExpired = true;
      try {
        const body = await res.clone().json();
        sessionExpired = body && (body.error === "No token" || body.error === "Invalid token");
      } catch { sessionExpired = true; }
      if (sessionExpired) { localStorage.removeItem("metago_token"); setAuthed(false); throw new Error("Unauthorized"); }
    }
    return res;
  }, []);

  // ── Background tasks & notifications ──
  const [bgTasks, setBgTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    if (!authed) return;
    const token = localStorage.getItem("metago_token");
    if (!token) return;

    let ctrl;
    let reconnectTimer;
    let stopped = false;

    // Throttle task_progress: часті тіки прогресу коалесимо в ≤1 оновлення стану / 600мс,
    // щоб фонова задача не перемальовувала весь застосунок і не «смикала» сторінку під час редагування.
    let pendingProgress = {};   // { [taskId]: progress }
    let lastFlush = 0;
    let progressTimer = null;
    const flushProgress = () => {
      progressTimer = null;
      const ids = Object.keys(pendingProgress);
      if (!ids.length) return;
      const snap = pendingProgress; pendingProgress = {};
      lastFlush = Date.now();
      setBgTasks(prev => prev.map(t => (snap[t.id] !== undefined ? { ...t, progress: snap[t.id] } : t)));
    };

    const connect = () => {
      if (stopped) return;
      ctrl = new AbortController();
      fetch("/api/tasks/events", {
        headers: { Authorization: `Bearer ${token}` },
        signal: ctrl.signal,
      }).then(async (resp) => {
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          let currentEvent = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              const dataStr = line.slice(6);
              try {
                const data = JSON.parse(dataStr);
                if (currentEvent === "init") {
                  setBgTasks(Array.isArray(data) ? data : []);
                } else if (currentEvent === "task_created") {
                  setBgTasks(prev => [...prev.filter(t => t.id !== data.id), data]);
                } else if (currentEvent === "task_progress") {
                  // Коалесимо прогрес: остання версія на задачу, флаш не частіше ніж раз/600мс.
                  pendingProgress[data.id] = data.progress;
                  if (Date.now() - lastFlush >= 600) flushProgress();
                  else if (!progressTimer) progressTimer = setTimeout(flushProgress, 600);
                } else if (currentEvent === "task_done") {
                  setBgTasks(prev => prev.map(t => t.id === data.id ? { ...t, status: "done" } : t));
                  setNotifications(prev => [...prev, { id: uid(), type: "success", title: data.title, taskId: data.id, projectId: data.projectId, taskType: data.type, ts: Date.now() }]);
                } else if (currentEvent === "task_error") {
                  setBgTasks(prev => prev.map(t => t.id === data.id ? { ...t, status: "error", error: data.error } : t));
                  setNotifications(prev => [...prev, { id: uid(), type: "error", title: data.title, error: data.error, ts: Date.now() }]);
                }
              } catch {}
              currentEvent = "";
            } else if (line.trim() === "" || line.startsWith(":")) {
              currentEvent = "";
            }
          }
        }
      }).catch(() => {}).finally(() => {
        if (!stopped) reconnectTimer = setTimeout(connect, 3000);
      });
    };

    connect();

    return () => {
      stopped = true;
      if (ctrl) ctrl.abort();
      clearTimeout(reconnectTimer);
      if (progressTimer) clearTimeout(progressTimer);
    };
  }, [authed]);

  // Auto-dismiss notifications after 8s
  useEffect(() => {
    if (!notifications.length) return;
    const timer = setTimeout(() => {
      setNotifications(prev => prev.filter(n => Date.now() - n.ts < 8000));
    }, 8000);
    return () => clearTimeout(timer);
  }, [notifications]);

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const runningTasksCount = useMemo(() => bgTasks.filter(t => t.status === "running").length, [bgTasks]);

  // ── Load projects from API after auth (paginated) ──
  const [projectsTotal, setProjectsTotal] = useState(0);
  const [projectsSearch, setProjectsSearch] = useState("");
  const [projectsLoading, setProjectsLoading] = useState(false);

  const loadProjects = useCallback(async (search = "", offset = 0, append = false) => {
    setProjectsLoading(true);
    try {
      const qs = `?limit=50&offset=${offset}${search ? `&search=${encodeURIComponent(search)}` : ""}`;
      const r = await apiFetch(`/api/projects${qs}`);
      const data = await r.json();
      if (data.projects) {
        setProjects(prev => append ? [...prev, ...data.projects] : data.projects);
        setProjectsTotal(data.total || 0);
      } else if (Array.isArray(data)) {
        // Backward compat
        setProjects(data);
        setProjectsTotal(data.length);
      }
    } catch {}
    setProjectsLoading(false);
  }, [apiFetch]);

  const loadMoreProjects = useCallback(() => {
    loadProjects(projectsSearch, projects.length, true);
  }, [loadProjects, projectsSearch, projects.length]);

  useEffect(() => {
    if (!authed) return;
    loadProjects();
  }, [authed, loadProjects]);

  const flash = msg => { setToast(msg); setTimeout(() => setToast(""), 3000); };
  const proj = useMemo(() => projects.find(p => p.id === activeId), [projects, activeId]);
  const getStore = id => dataRef.current[id] || { rows: [], clusters: [] };
  const setStore = (id, d) => { dataRef.current[id] = d; };

  // ── Load all project data from API ──
  const loadProjectData = useCallback(async (id) => {
    try {
      const [clustersRes, contentRes, structRes, semRes, linksRes] = await Promise.all([
        apiFetch(`/api/projects/${id}/clusters`).then(r => r.json()),
        apiFetch(`/api/projects/${id}/content`).then(r => r.json()),
        apiFetch(`/api/projects/${id}/structure`).then(r => r.json()),
        apiFetch(`/api/projects/${id}/semclusters`).then(r => r.json()),
        apiFetch(`/api/projects/${id}/links`).then(r => r.json()),
      ]);
      const clusters = Array.isArray(clustersRes) ? clustersRes : [];
      const rows = clusters.flatMap(c => (c.keywords || []).map(k => ({ keyword: k.keyword, frequency: k.frequency, url: c.url })));
      const contentArr = Array.isArray(contentRes) ? contentRes : [];
      const contentByCluster = {};
      contentArr.forEach(ci => { if (ci.clusterId) contentByCluster[ci.clusterId] = ci; });
      const mergedClusters = clusters.map(c => {
        const ci = contentByCluster[c.id];
        return ci ? { ...c, content: ci.content, cSt: ci.status === "READY" ? null : ci.status, cErr: ci.error || "" } : c;
      });
      const store = { rows, clusters: mergedClusters };
      if (structRes) store.structure = structRes;
      if (semRes && (Array.isArray(semRes) ? semRes.length : Object.keys(semRes).length)) store.semClusters = Array.isArray(semRes) ? semRes : semRes.clusters || [];
      dataRef.current[id] = store;
      const linksArr = Array.isArray(linksRes) ? linksRes : [];
      if (linksArr.length) dataRef.current[id + "_links"] = { rows: linksArr, projectName: "" };
    } catch (err) {
      console.error("Load project data error:", err);
    }
  }, [apiFetch]);

  const openP = async (id) => { setActiveId(id); await loadProjectData(id); setView("dashboard"); };

  // Restore active project data after refresh
  useEffect(() => {
    if (!authed || !projects.length || !activeId) return;
    if (projects.some(p => p.id === activeId) && !dataRef.current[activeId]?.clusters) {
      loadProjectData(activeId);
    }
  }, [authed, projects, activeId, loadProjectData]);

  // Resolve a saved active project even if it's not on the first (paginated) page of `projects`,
  // so a page refresh keeps the current view instead of jumping back to the projects list.
  const resolveRef = useRef(null);
  useEffect(() => {
    if (!authed || !activeId || !projects.length) return;
    if (projects.some(p => p.id === activeId)) { resolveRef.current = null; return; }
    if (resolveRef.current === activeId) return;
    resolveRef.current = activeId;
    apiFetch(`/api/projects/${activeId}`)
      .then(r => (r.ok ? r.json() : null))
      .then(p => {
        if (p && p.id) setProjects(prev => prev.some(x => x.id === p.id) ? prev : [p, ...prev]);
        else { setActiveId(null); setView("list"); }
      })
      .catch(() => { setActiveId(null); setView("list"); });
  }, [authed, activeId, projects, apiFetch]);

  // ── Save helpers ──
  const saveClusters = useCallback(async (projectId, clusters) => {
    try { await apiFetch(`/api/projects/${projectId}/clusters`, { method: "PUT", body: JSON.stringify({ clusters }) }); } catch (e) { console.error("Save clusters:", e); }
  }, [apiFetch]);
  const saveContent = useCallback(async (projectId, items) => {
    try { await apiFetch(`/api/projects/${projectId}/content`, { method: "PUT", body: JSON.stringify({ items }) }); } catch (e) { console.error("Save content:", e); }
  }, [apiFetch]);
  const saveStructure = useCallback(async (projectId, tree) => {
    try { await apiFetch(`/api/projects/${projectId}/structure`, { method: "PUT", body: JSON.stringify({ tree }) }); } catch (e) { console.error("Save structure:", e); }
  }, [apiFetch]);
  const saveSemClusters = useCallback(async (projectId, data) => {
    try { await apiFetch(`/api/projects/${projectId}/semclusters`, { method: "PUT", body: JSON.stringify({ data: { clusters: data } }) }); } catch (e) { console.error("Save semclusters:", e); }
  }, [apiFetch]);
  const saveLinks = useCallback(async (projectId, items) => {
    // Safety: never auto-wipe. An empty payload would delete all of the project's articles server-side.
    if (!Array.isArray(items) || items.length === 0) { console.warn("saveLinks: skipping empty payload"); return; }
    try { await apiFetch(`/api/projects/${projectId}/links`, { method: "PUT", body: JSON.stringify({ items }) }); } catch (e) { console.error("Save links:", e); }
  }, [apiFetch]);

  // ── Theme (memoized) ──
  const t = useMemo(() => dark ? {
    bg: "#0a0a0c", bgCard: "#14141e", bgSolid: "#111114", border: "rgba(255,255,255,0.06)", text: "#ededf0", textMuted: "#5c5c72",
    inputBg: "rgba(255,255,255,0.04)", inputBorder: "rgba(255,255,255,0.08)", badge: "rgba(255,255,255,0.05)", hover: "rgba(255,255,255,.04)",
    accent: "#3b82f6", accentMuted: "rgba(59,130,246,0.1)", glass: "rgba(255,255,255,0.02)"
  } : {
    bg: "#f5f5f7", bgCard: "#ffffff", bgSolid: "#fff", border: "rgba(0,0,0,0.06)", text: "#0a0a0f", textMuted: "#71717a",
    inputBg: "rgba(255,255,255,0.8)", inputBorder: "rgba(0,0,0,0.1)", badge: "rgba(0,0,0,0.04)", hover: "rgba(0,0,0,.03)",
    accent: "#2563eb", accentMuted: "rgba(37,99,235,0.06)", glass: "rgba(255,255,255,0.5)"
  }, [dark]);

  // ── Styles (memoized) ──
  const card = useMemo(() => ({ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: 18, marginBottom: 14, backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }), [t]);
  const inp = useMemo(() => ({ border: `1px solid ${t.inputBorder}`, borderRadius: 6, padding: "7px 11px", fontSize: 13, outline: "none", width: "100%", fontFamily: "inherit", background: t.inputBg, color: t.text, backdropFilter: "blur(8px)", transition: "border-color .15s, box-shadow .15s" }), [t]);
  const btnP = useMemo(() => ({ background: t.accent, color: "#fff", border: "none", borderRadius: 6, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, letterSpacing: "0.01em", transition: "opacity .15s, transform .1s" }), [t]);
  const btnG = useMemo(() => ({ background: "transparent", color: t.text, border: `1px solid ${t.border}`, borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, transition: "border-color .15s, background .15s" }), [t]);
  const btnSm = useMemo(() => ({ ...btnG, padding: "4px 8px", fontSize: 11 }), [btnG]);
  const stitle = useMemo(() => ({ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: t.textMuted, margin: "0 0 8px" }), [t]);
  const muted = useMemo(() => ({ color: t.textMuted, fontSize: 12 }), [t]);
  const th = useMemo(() => ({ textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, color: t.textMuted, borderBottom: `1px solid ${t.border}`, whiteSpace: "nowrap" }), [t]);
  const td = useMemo(() => ({ padding: "7px 10px", borderBottom: `1px solid ${t.border}`, verticalAlign: "top", fontSize: 13 }), [t]);

  const value = useMemo(() => ({
    // Navigation
    view, setView, goBack, canGoBack,
    // Projects
    projects, setProjects, activeId, setActiveId, proj, infoId, setInfoId,
    openP, loadProjectData,
    // Auth
    authed, authLoading, currentUser, userRole,
    loginUser, setLoginUser, loginPass, setLoginPass, loginError, setLoginError,
    loginPending, loginNeedCode, loginCodeInput, setLoginCodeInput,
    handleLogin, handleVerifyCode, handleLogout,
    // Data
    getStore, setStore, dataRef,
    // API
    apiFetch,
    // Save helpers
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    // UI
    flash, toast, dark, setDark,
    // Theme & styles
    t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    // Tasks & notifications
    bgTasks, notifications, dismissNotification, runningTasksCount,
    // Projects pagination
    projectsTotal, projectsSearch, setProjectsSearch, projectsLoading, loadProjects, loadMoreProjects,
  }), [view, projects, activeId, proj, infoId, authed, authLoading, currentUser, userRole,
    loginUser, loginPass, loginError, loginPending, loginNeedCode, loginCodeInput,
    toast, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    bgTasks, notifications, runningTasksCount, projectsTotal, projectsSearch, projectsLoading,
    apiFetch, loadProjectData, loadProjects, loadMoreProjects, saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks, dismissNotification]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
